import { api, Env, PageScheduler, TPageProps, TPageSequence, useApiPlay, useApiState } from "@ancademy/vse-client";
import { ISubGame } from "@ancademy/vse-share";
import { ContainerOutlined, FileTextOutlined } from "@ant-design/icons";
import {
  Background,
  BtnGroup,
  Color,
  Em,
  getSubGameType,
  KnowledgePointList,
  KnowledgePoints,
  Md,
  showMsg,
  testPageCtx,
  TSubPlayProps,
} from "@client";
import { IntroContent, KnowledgeFile } from "@client/component";
import { MsgHistory } from "@client/page/MsgHistory";
import { Style } from "@client/style";
import { useAsyncEffect } from "@client/util";
import { css, cx } from "@emotion/css";
import { Npc, NpcModel } from "@npc";
import { IMainPlayerState, ISubCreateParams, ISubPlayerState, SubNamespace, TResult } from "@share";
import { Button, Col, Modal, Row, Space } from "antd";
import { mean } from "lodash";
import React, { createContext, useContext, useEffect, useState } from "react";

export type TPlayContext = {
  round: number;
  params: ISubCreateParams;
  playerState: ISubPlayerState;
  saveResult: (result: TResult) => void;
  config: ISubPlayConfig;
};

export type IPlayProps = { playDone: () => void };

export type ITestProps = {};

export interface ISubPlayConfig {
  namespace: SubNamespace;
  withSearch?: boolean;
  label: string;
  npcModel: NpcModel;
  initState: (state: Partial<ISubPlayerState>) => void;
  npcWelcome?: () => Promise<void>;
  knowledgeMsg?: () => Promise<void>;
  introMsg?: (params: ISubCreateParams) => string | React.ReactNode[];
  Play: React.ComponentType<IPlayProps>;
  Test?: React.ComponentType<ITestProps>;
  bg?: string;
}

export const playContext = createContext<TPlayContext>(null!);

function usePageData(): ISubCreateParams & ISubPlayConfig & { gameId: string; mainGameId: string } {
  const { game } = useApiPlay<ISubPlayerState, ISubCreateParams>();
  const { config } = useContext(playContext);
  return { ...config, ...game.params, gameId: game.id, mainGameId: (game as ISubGame<ISubCreateParams>).mainGameId };
}

function InitPage({ nextPage }: TPageProps) {
  const { initState, mainGameId, npcModel } = usePageData();
  const { apiState, setApiState, game } = useApiPlay<ISubPlayerState, ISubCreateParams>();
  const { loading, apiState: mainState } = useApiState<IMainPlayerState>(mainGameId);
  useEffect(() => {
    if (loading) {
      return;
    }
    if (apiState.inited) {
      return;
    }
    setApiState((s) => {
      initState(s);
      s.inited = true;
      const gameIndex = mainState.subGameList.findIndex((s) => s.id === game.id);
      const nextGame = mainState.subGameList[gameIndex + 1];
      s.nextGameUrl = nextGame ? `/${nextGame.namespace}/${nextGame.id}` : `/play/${mainGameId}`;
      Npc.setModel(npcModel);
    });
    nextPage();
  }, [loading]);
  return null;
}

function BackgroundPage({ nextPage }: TPageProps) {
  const { npcWelcome, showIntro } = usePageData();
  useAsyncEffect(async () => {
    if (!showIntro) {
      return nextPage();
    }
    Npc.setVisible(true);
    await npcWelcome?.();
    nextPage();
  }, []);
  return null;
}

function IntroPage({ nextPage }: TPageProps) {
  const { game } = useApiPlay<ISubPlayerState, ISubCreateParams>();
  const { label, introMsg, showIntro, withSearch } = usePageData(),
    skip = !(introMsg && showIntro);
  useEffect(() => {
    if (skip) {
      nextPage();
    }
    return () => Npc.setVisible(false);
  }, []);
  if (skip) {
    return null;
  }
  return (
    <IntroContent
      msg={introMsg(game.params)}
      title={`【${label}】任务说明`}
      visible
      onOk={nextPage}
      withSearchTips={withSearch}
    />
  );
}

namespace PlayPage {
  export function PlayPage({ nextPage }: TPageProps) {
    const { Play, introMsg, knowledgeMsg, showTest, Test } = usePageData();
    const [showPlay, setShowPlay] = useState(false);
    useAsyncEffect(async () => {
      await showMsg(
        <>
          你可以随时点击右上角按钮了解本环节的
          {introMsg && (
            <>
              <Em>任务说明</Em>和
            </>
          )}
          <Em>知识点</Em>。
        </>,
        {
          okText: "知道了",
        }
      );
      setShowPlay(true);
    }, []);
    return (
      <>
        <Header />
        {showPlay && (
          <Play
            playDone={async () => {
              setShowPlay(false);
              if (showTest) {
                if (knowledgeMsg) {
                  await knowledgeMsg();
                } else {
                  await showMsg(`接下来，将回顾本场景涉及的知识点${Test ? "并进行一个小测验" : ""}。`, {
                    okText: "开始",
                  });
                }
              } else {
                await showMsg("本轮结束！", {
                  okText: "下一轮",
                });
              }
              nextPage();
            }}
          />
        )}
      </>
    );
  }

  function Header() {
    const { mainGameId, practice, searchCost, r, label, namespace, withSearch } = usePageData();
    return (
      <Row className={Style.header}>
        <Col span={7}>
          <button className={Style.btnPrimarySm} onClick={() => (location.href = `${Env.basePath}/play/${mainGameId}`)}>
            返回
          </button>
        </Col>
        <Col
          span={10}
          className={css`
            text-align: center;
          `}
        >
          {getSubGameType(namespace).label}&nbsp;-&nbsp;{label}
          <span
            className={css`
              font-size: 1.5rem;
            `}
          >
            &nbsp;({practice ? "练习轮" : `第${r}轮`}
            {withSearch ? "——" + (searchCost ? `搜寻成本为${searchCost}` : "无搜寻成本") : ""})
          </span>
        </Col>
        <Col
          span={7}
          className={css`
            text-align: right;
          `}
        >
          <Space>
            <HeaderIntro />
            <KnowledgePoints />
            <HeaderScore />
          </Space>
        </Col>
      </Row>
    );
  }

  function HeaderIntro() {
    const { game } = useApiPlay<ISubPlayerState, ISubCreateParams>(),
      { introMsg, withSearch, label } = usePageData();
    const [visible, setVisible] = useState(false);
    if (!introMsg) {
      return null;
    }
    return (
      <>
        <Button className={Style.btnPrimarySm} icon={<FileTextOutlined />} onClick={() => setVisible(true)}>
          任务说明
        </Button>
        <IntroContent
          msg={introMsg(game.params)}
          title={`【${label}】任务说明`}
          visible={visible}
          onOk={() => setVisible(false)}
          withSearchTips={withSearch}
        />
      </>
    );
  }

  function HeaderScore() {
    const { r } = usePageData();
    const [visible, setVisible] = useState(false);
    if (r <= 1) {
      return null;
    }
    return (
      <>
        <Button className={Style.btnPrimarySm} icon={<ContainerOutlined />} onClick={() => setVisible(true)}>
          表现得分
        </Button>
        <Modal
          {...Md.pageProps}
          title={false}
          visible={visible}
          cancelButtonProps={{ hidden: true }}
          onOk={() => setVisible(false)}
        >
          <ScoreTable />
        </Modal>
      </>
    );
  }

  export function ScoreTable() {
    const { mainGameId, namespace } = usePageData();
    const main = useApiState<IMainPlayerState>(mainGameId);
    if (main.loading) {
      return null;
    }
    const scoreList = main.apiState.subGameList
      .filter((s) => s.namespace === namespace)
      .map((s) => s.score)
      .slice(1);
    return (
      scoreList.length > 1 && (
        <>
          <h3 className={Style.modalText}>各轮次表现得分：</h3>
          <table
            className={css`
              margin: 2rem auto;

              th,
              td {
                padding: 1rem 2rem;
                border: 1px solid ${Color.light};
              }
            `}
          >
            <thead>
              <tr>
                {scoreList.map((_, i) => (
                  <th key={i}>{`第${i + 1}轮`}</th>
                ))}
                <th>平均分</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {scoreList.map((score, i) => (
                  <td key={i}>{score || "-"}</td>
                ))}
                <td>{mean(scoreList.filter((s) => s)).toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )
    );
  }
}

function KnowledgePage({ nextPage }: TPageProps) {
  const { namespace, showTest } = usePageData();
  const items = KnowledgePointList.filter((s) => namespace === s.namespace) || [];
  const skip = !(showTest && items.length);
  const [itemIndex, setItemIndex] = useState(0),
    over = itemIndex >= items.length - 1;
  useEffect(() => {
    if (skip) {
      nextPage();
    }
  }, []);
  if (skip) {
    return null;
  }
  const { fileName } = items[itemIndex];
  return (
    <Modal
      {...Md.pageProps}
      visible={true}
      title="知识点"
      cancelButtonProps={{ hidden: !over || items.length <= 1, className: Style.btnWarningSm }}
      cancelText="再读一遍"
      onCancel={() => setItemIndex(0)}
      okText={over ? "完成阅读" : "下一条"}
      onOk={() => (over ? nextPage() : setItemIndex(itemIndex + 1))}
    >
      <div
        className={css`
          height: 70vh;
        `}
      >
        <KnowledgeFile fileName={fileName} />
      </div>
    </Modal>
  );
}

function TestPage({ nextPage }: TPageProps) {
  const { setApiState } = useApiPlay<ISubPlayerState>();
  const { Test, showTest, mainGameId, namespace } = usePageData(),
    skip = !(showTest && Test);
  useEffect(() => {
    if (skip) {
      nextPage();
    }
  }, []);
  if (skip) {
    return null;
  }
  return (
    <testPageCtx.Provider
      value={{
        done: async (answers, answerScores) => {
          setApiState((s) => {
            s.answers = answers;
            s.answerScores = answerScores;
          });
          nextPage();
        },
        getDownloadData: async () => {
          const {
            data: { subGameList },
          } = (await api.getMyState(mainGameId)) as any as {
            data: IMainPlayerState;
          };
          return Promise.all(
            subGameList
              .filter((s) => s.namespace === namespace)
              .map(async ({ id, r }) => {
                const res = await api.getMyState(id);
                const data: any = {
                  round: r + 1,
                  createdAt: res.createdAt,
                };
                for (const [key, val] of Object.entries(res.data)) {
                  data[key] = JSON.stringify(val);
                }
                return data;
              })
          );
        },
      }}
    >
      <Test />
    </testPageCtx.Provider>
  );
}

function TestEndPage({ nextPage }: TPageProps) {
  const { Test, showTest } = usePageData(),
    skip = !(showTest && Test);
  useAsyncEffect(async () => {
    if (!skip) {
      await showMsg("小测试与数据分析完成，本场景任务结束！", {
        okText: "下一步",
      });
    }
    nextPage();
  }, []);
  return null;
}

function EndPage({ nextPage }: TPageProps) {
  const { gameId, mainGameId } = usePageData();
  const sub = useApiPlay<ISubPlayerState>(),
    main = useApiState<IMainPlayerState>(mainGameId);
  useEffect(() => {
    if (main.loading) {
      return;
    }
    main.setApiState((s) => {
      const { subGameList } = s;
      const curGame = subGameList.find((s) => s.id === gameId);
      curGame.over = true;
      curGame.score = sub.apiState.result?.score;
      setTimeout(() => {
        nextPage();
      }, 200);
    });
  }, [main.loading]);
  return null;
}

function OverPage({}: TPageProps) {
  const { mainGameId, namespace, showTest, label, stepInfo } = usePageData();
  const sub = useApiPlay<ISubPlayerState>();
  const main = useApiState<IMainPlayerState>(mainGameId);
  const isLastGame = sub.apiState.nextGameUrl.includes(mainGameId),
    nextGameUrl = `${Env.basePath}${sub.apiState.nextGameUrl}`;
  useEffect(() => {
    if (!showTest) {
      location.href = nextGameUrl;
    }
  }, []);
  useEffect(() => {
    if (!stepInfo) {
      return;
    }
    const { resultId, stepResult, stepAmount, questionScore, behaviorScore } = stepInfo;
    if (main.loading) {
      return;
    }
    const scoreList = main.apiState.subGameList
        .filter((s) => s.namespace === namespace)
        .map((s) => s.score)
        .slice(1),
      meanScore = mean(scoreList),
      score = ~~((meanScore ? meanScore / 100 : 1) * behaviorScore) + questionScore;
    api
      .saveStepResult({
        id: resultId,
        stepResult: { ...stepResult, score },
        stepAmount,
      })
      .catch((err) => console.log(err));
  }, [main.loading]);
  return (
    showTest && (
      <Modal {...Md.pageProps} footer={false}>
        <PlayPage.ScoreTable />
        <p
          className={cx(
            Style.modalText,
            css`
              text-align: center;
            `
          )}
        >
          您已完成
          <Em
            className={css`
              font-size: 2.5rem;
            `}
          >
            {label}
          </Em>
          场景实验，接下来进入：
        </p>
        <BtnGroup>
          <Button className={Style.btnWarningSm} onClick={() => (location.href = `${Env.basePath}/play/${mainGameId}`)}>
            返回主页
          </Button>
          {isLastGame || (
            <Button className={Style.btnPrimarySm} onClick={() => (location.href = nextGameUrl)}>
              下个环节
            </Button>
          )}
        </BtnGroup>
      </Modal>
    )
  );
}

const pageSequence: TPageSequence = [
  InitPage,
  BackgroundPage,
  IntroPage,
  PlayPage.PlayPage,
  KnowledgePage,
  EndPage,
  TestPage,
  TestEndPage,
  OverPage,
];

function BtnNewDemo() {
  const { game } = useApiPlay(),
    { namespace } = usePageData();
  return Env.isProd ? null : (
    <Button
      className={cx(
        Style.btnPrimaryXS,
        css`
          position: fixed;
          z-index: 2000;
          bottom: 0;
          right: 0;
        `
      )}
      onClick={async () => {
        const newGame: ISubGame<any> = await api.addSubGame({ ...(game as any) });
        location.href = `${Env.basePath}/${newGame.namespace}/${newGame.id}`;
      }}
    >
      New {namespace}
    </Button>
  );
}

export function SubPlay(props: TSubPlayProps & { config: ISubPlayConfig }) {
  const { game, config } = props;
  const { apiState, setApiState } = useApiPlay<ISubPlayerState, ISubCreateParams>();
  return (
    <>
      <Background src={config.bg} />
      <MsgHistory />
      <playContext.Provider
        value={
          {
            config,
            round: game.params.r,
            params: game.params,
            playerState: apiState,
            game,
            saveResult: (result: TResult) => setApiState((s) => (s.result = result)),
          } as any
        }
      >
        <BtnNewDemo />
        <PageScheduler
          gameId={game.id}
          pageSequence={pageSequence}
          onOver={async () => {
            console.log("OVER---", game);
          }}
        />
      </playContext.Provider>
    </>
  );
}
